import { TAXONOMY_DISPLAY } from '@archetelos/lib/taxonomy';
import { queryOptions } from '@tanstack/react-query';

import type { Tables, Views } from '@archetelos/db';
import type { ArticleDoc } from '@archetelos/editor';

import { supabase } from '@/lib/supabase';

export type Content = Tables<'content'>;
export type ContentVersion = Tables<'content_versions'>;
export type CreatorAnalyticsRow = Views<'creator_content_analytics'>;
export type AccessTier = 'always_free' | 'metered' | 'members_only';

// Every content column EXCEPT body_json (no longer directly selectable — the
// article body is served only via get_article_render / get_authored_content). Keep in sync with
// the content table when columns are added.
export const CONTENT_NO_BODY_FIELDS =
  'id, creator_id, type, title, slug, excerpt, video_bunny_id, cover_image_url, tradition_tags, topic_tags, status, reviewer_id, reviewer_notes, published_at, created_at, updated_at, cover_image_alt, gate_policy, is_editors_pick, editors_pick_at, editors_pick_by, category_id, is_featured_vod, featured_vod_at, featured_vod_by, access_tier';

/** Server-decided reader render: full body or a preview slice, entitlement on the server. */
export interface ArticleRender {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  publishedAt: string | null;
  accessTier: AccessTier;
  bodyJson: ArticleDoc | null;
  gated: boolean;
  isTruncated: boolean;
  remaining: number;
  isMember: boolean;
  unlocked: boolean;
}

/** A related-essay row for the end-of-reader rail (reader's own idiom). */
export interface RailArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  publishedAt: string | null;
}
export interface RelatedArticles {
  heading: string;
  articles: RailArticle[];
}

const RAIL_FIELDS = 'id, slug, title, excerpt, published_at';
function toRailArticle(r: {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  published_at: string | null;
}): RailArticle {
  return {
    id: r.id,
    slug: r.slug,
    title: r.title,
    excerpt: r.excerpt,
    publishedAt: r.published_at,
  };
}

export const contentQueries = {
  listForCreator: (creatorId: string | undefined) =>
    queryOptions({
      queryKey: ['content', 'creator', creatorId] as const,
      queryFn: async (): Promise<Content[]> => {
        if (!creatorId) return [];
        const { data, error } = await supabase
          .from('content')
          .select(CONTENT_NO_BODY_FIELDS)
          .eq('creator_id', creatorId)
          .order('updated_at', { ascending: false });
        if (error) throw error;
        return (data ?? []) as unknown as Content[];
      },
      enabled: Boolean(creatorId),
    }),

  // Owner/admin full row (incl. body_json) via the SECURITY DEFINER RPC, so the
  // editor + preview + admin review keep working after the body_json revoke.
  byId: (contentId: string | undefined) =>
    queryOptions({
      queryKey: ['content', 'id', contentId] as const,
      queryFn: async (): Promise<Content | null> => {
        if (!contentId) return null;
        const { data, error } = await supabase
          .rpc('get_authored_content', { p_id: contentId })
          .maybeSingle();
        if (error) throw error;
        return (data as Content | null) ?? null;
      },
      enabled: Boolean(contentId),
    }),

  // Reader render — server decides full vs preview (replaces the old bySlug
  // select('*'), which would now error on the body_json revoke).
  articleRender: (slug: string | undefined) =>
    queryOptions({
      queryKey: ['article-render', slug] as const,
      queryFn: async (): Promise<ArticleRender | null> => {
        if (!slug) return null;
        const { data, error } = await supabase
          .rpc('get_article_render', { p_slug: slug })
          .maybeSingle();
        if (error) throw error;
        if (!data) return null;
        return {
          id: data.id,
          slug: data.slug,
          title: data.title,
          excerpt: data.excerpt,
          coverImageUrl: data.cover_image_url,
          coverImageAlt: data.cover_image_alt,
          publishedAt: data.published_at,
          accessTier: data.access_tier as AccessTier,
          bodyJson: data.body_json as ArticleDoc | null,
          gated: data.gated,
          isTruncated: data.is_truncated,
          remaining: data.remaining,
          isMember: data.is_member,
          unlocked: data.unlocked,
        };
      },
      enabled: Boolean(slug),
    }),

  /**
   * End-of-reader rail: published essays sharing the current piece's first
   * tradition tag (newest first, minus the current id, 3 items). No tag or fewer
   * than two matches → latest essays overall. Reads anon-granted content columns.
   */
  relatedArticles: (currentId: string | undefined) =>
    queryOptions({
      queryKey: ['related-articles', currentId] as const,
      queryFn: async (): Promise<RelatedArticles> => {
        const fallback = async (): Promise<RelatedArticles> => {
          const { data, error } = await supabase
            .from('content')
            .select(RAIL_FIELDS)
            .eq('type', 'article')
            .eq('status', 'published')
            .neq('id', currentId!)
            .order('published_at', { ascending: false })
            .limit(3);
          if (error) throw error;
          return { heading: 'More from ArcheTelos', articles: (data ?? []).map(toRailArticle) };
        };

        const { data: current, error: currentError } = await supabase
          .from('content')
          .select('tradition_tags')
          .eq('id', currentId!)
          .maybeSingle();
        if (currentError) throw currentError;
        const tag = current?.tradition_tags?.[0] ?? null;
        if (!tag) return fallback();

        const { data, error } = await supabase
          .from('content')
          .select(RAIL_FIELDS)
          .eq('type', 'article')
          .eq('status', 'published')
          .contains('tradition_tags', [tag])
          .neq('id', currentId!)
          .order('published_at', { ascending: false })
          .limit(3);
        if (error) throw error;
        const matched = (data ?? []).map(toRailArticle);
        // Fewer than two tradition-matched → the rail would read thin; fall back.
        if (matched.length < 2) return fallback();
        return { heading: `More from ${TAXONOMY_DISPLAY[tag] ?? tag}`, articles: matched };
      },
      enabled: Boolean(currentId),
    }),

  versions: (contentId: string | undefined) =>
    queryOptions({
      queryKey: ['content-versions', contentId] as const,
      queryFn: async (): Promise<ContentVersion[]> => {
        if (!contentId) return [];
        const { data, error } = await supabase
          .from('content_versions')
          .select('*')
          .eq('content_id', contentId)
          .order('version_number', { ascending: false })
          .limit(40);
        if (error) throw error;
        return (data ?? []) as ContentVersion[];
      },
      enabled: Boolean(contentId),
    }),

  slugAvailable: (slug: string, excludeId: string | undefined) =>
    queryOptions({
      queryKey: ['slug-available', slug, excludeId] as const,
      queryFn: async (): Promise<boolean> => {
        let q = supabase.from('content').select('id').eq('slug', slug);
        if (excludeId) q = q.neq('id', excludeId);
        const { data, error } = await q.maybeSingle();
        if (error && error.code !== 'PGRST116') throw error;
        return !data;
      },
      enabled: slug.trim().length >= 3,
      staleTime: 5000,
    }),

  // Creator engagement (Spec 26). One factory, one queryKey shape — so the UI's
  // range switch is a clean source swap with no union-typed useQuery.
  //   'all'  → get_creator_analytics (lifetime matview, refreshed nightly)
  //   7|30|90 → get_creator_analytics_windowed (always-live, lane-2
  //             time_spent_logs over the window)
  // Same row shape (CreatorAnalyticsRow) either way, so every chart maps over it
  // unchanged. Default 'all' preserves the no-arg callers (creator.index).
  analytics: (range: 'all' | 7 | 30 | 90 = 'all') =>
    queryOptions({
      queryKey: ['creator-analytics', range] as const,
      queryFn: async (): Promise<CreatorAnalyticsRow[]> => {
        const { data, error } =
          range === 'all'
            ? await supabase.rpc('get_creator_analytics')
            : await supabase.rpc('get_creator_analytics_windowed', { p_days: range });
        if (error) throw error;
        return (data ?? []) as CreatorAnalyticsRow[];
      },
    }),
};
